/**
 * 🔌 CENTRALIZED SOCKET MANAGER
 *
 * This service manages all socket connections across the application.
 * It ensures sockets are properly connected/disconnected based on auth state.
 *
 * Socket Services Managed:
 * - socketService (collaborators namespace)
 * - messagingSocketService (messaging namespace)
 * - roomSocketService (research-rooms namespace)
 * - communitySocketService (community namespace)
 */

import { getAuthToken } from "../utils/apiUtils";
import socketService from "./socketService";
import messagingSocketService from "./messagingSocketService";
import roomSocketService from "./rooms/roomSocketService";
import communitySocketService from "./communitySocketService";

class SocketManager {
  constructor() {
    this.isInitialized = false;
    this.connectionStatus = {
      collaborators: false,
      messaging: false,
      rooms: false,
      community: false,
    };
  }

  /**
   * Initialize all socket connections
   * Call this when user logs in or on app load with valid token
   */
  initializeAll() {
    const token = getAuthToken();

    if (!token) {
return false;
    }
try {
      // Initialize collaborators socket (main socket)
      if (!socketService.isConnected()) {
        const socket = socketService.connect(token);
        this.connectionStatus.collaborators = !!socket;
} else {
this.connectionStatus.collaborators = true;
      }

      // Initialize messaging socket
      if (!messagingSocketService.isSocketConnected()) {
        const socket = messagingSocketService.connect();
        this.connectionStatus.messaging = !!socket;
} else {
this.connectionStatus.messaging = true;
      }

      // Initialize room socket
      if (!roomSocketService.isConnected()) {
        const socket = roomSocketService.connect(token);
        this.connectionStatus.rooms = !!socket;
} else {
this.connectionStatus.rooms = true;
      }

      // Community socket is connected on-demand when user visits a community
      // We don't auto-connect it here
this.isInitialized = true;
return true;
    } catch (error) {
return false;
    }
  }

  /**
   * Disconnect all socket connections
   * Call this when user logs out
   */
  disconnectAll() {
try {
      socketService.disconnect();
} catch (e) { /* ignored */ }

    try {
      messagingSocketService.disconnect();
} catch (e) { /* ignored */ }

    try {
      roomSocketService.disconnect();
} catch (e) { /* ignored */ }

    try {
      communitySocketService.disconnect();
} catch (e) { /* ignored */ }

    this.isInitialized = false;
    this.connectionStatus = {
      collaborators: false,
      messaging: false,
      rooms: false,
      community: false,
    };
}

  /**
   * Get current connection status
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      connections: {
        collaborators: socketService.isConnected?.() || false,
        messaging: messagingSocketService.isSocketConnected?.() || false,
        rooms: roomSocketService.isConnected?.() || false,
        community: communitySocketService.isSocketConnected?.() || false,
      },
    };
  }

  /**
   * Debug socket connections
   */
  debugStatus() {
}

  /**
   * Reconnect all sockets (e.g., after token refresh)
   */
  reconnectAll() {
this.disconnectAll();

    // Small delay before reconnecting
    setTimeout(() => {
      this.initializeAll();
    }, 500);

    return true;
  }
}

// Create singleton instance
const socketManager = new SocketManager();

// Export for use in components and services
export default socketManager;

// Also export individual services for direct access
export {
  socketService,
  messagingSocketService,
  roomSocketService,
  communitySocketService,
};
